/**
 * Config API — admin-managed configurable lookup lists. Reads are open to any
 * authenticated user (forms source their dropdowns here); writes are ADMIN-only.
 * Which lists exist is code-defined on the backend (enums.ConfigList); the values
 * inside each list are maintained at runtime on the Settings page.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

/** One value within a configurable list. */
export interface ConfigOption {
  id: string;
  listKey: string;
  value: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

/** Create body — label required; value defaults to label (immutable key); sortOrder optional. */
export interface CreateConfigOptionPayload {
  label: string;
  value?: string | null;
  sortOrder?: number | null;
}

/** Update body — label + active required; sortOrder optional. The value key is immutable. */
export interface UpdateConfigOptionPayload {
  label: string;
  sortOrder?: number | null;
  active: boolean;
}

const configKey = (listKey: string, includeInactive: boolean) =>
  ['config', listKey, includeInactive] as const;

/**
 * Read a configurable list. Active values only by default (for form dropdowns);
 * pass includeInactive=true for the admin Settings view (shows deactivated rows).
 */
export function useConfigOptions(listKey: string, includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: configKey(listKey, includeInactive),
    queryFn: async (): Promise<ConfigOption[]> => {
      const qs = includeInactive ? '?includeInactive=true' : '';
      const res = await api.get<ApiEnvelope<ConfigOption[]>>(`/config/${listKey}${qs}`);
      return res.data.data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Returns a value→label resolver for a configurable list (e.g. to render a stored
 * account.type value as its friendly label). Includes deactivated values so records
 * carrying a now-hidden value still resolve; falls back to the raw value, then "—".
 */
export function useConfigLabels(listKey: string, enabled = true) {
  const { data: options = [] } = useConfigOptions(listKey, true, enabled);
  const byValue = new Map(options.map((o) => [o.value, o.label]));
  return (value: string | null | undefined): string =>
    value ? byValue.get(value) ?? value : '—';
}

// Invalidate by the ['config', listKey] prefix so both the active-only and the
// include-inactive caches refresh after any write.
function invalidateList(qc: ReturnType<typeof useQueryClient>, listKey: string) {
  void qc.invalidateQueries({ queryKey: ['config', listKey] });
}

export function useCreateConfigOption(listKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateConfigOptionPayload) =>
      api.post<ApiEnvelope<ConfigOption>>(`/config/${listKey}`, payload).then((res) => res.data.data),
    onSuccess: () => invalidateList(qc, listKey),
  });
}

export function useUpdateConfigOption(listKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateConfigOptionPayload }) =>
      api.put<ApiEnvelope<ConfigOption>>(`/config/${listKey}/${id}`, payload).then((res) => res.data.data),
    onSuccess: () => invalidateList(qc, listKey),
  });
}

export function useDeleteConfigOption(listKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<ApiEnvelope<void>>(`/config/${listKey}/${id}`).then((res) => res.data.data),
    onSuccess: () => invalidateList(qc, listKey),
  });
}
